// ============================================
// Premier Case Tracker — Auth Hook
// ============================================

import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import type { User, UserRole } from '../types';
import { ROLE_REDIRECTS } from '../types';

interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  register: (data: { firstName: string; lastName: string; email: string; teamId: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: sessionStorage.getItem('pct_token'),
    loading: true,
    error: null,
  });
  const navigate = useNavigate();

  // Load user on mount if token exists
  useEffect(() => {
    const loadUser = async () => {
      const token = sessionStorage.getItem('pct_token');
      if (!token) {
        setState((s) => ({ ...s, loading: false }));
        return;
      }
      try {
        const data = await api.get<any>('/auth/me');
        if (data.success) {
          const user: User = {
            id: data.id,
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            avatarUrl: data.avatarUrl,
            role: data.role,
            teamId: data.teamId,
            teamName: data.teamName,
            teamManagerFor: data.teamManagerFor,
            smeForTeam: data.smeForTeam,
            isActive: true,
            createdAt: data.createdAt,
          };
          setState({ user, token, loading: false, error: null });
        }
      } catch {
        sessionStorage.removeItem('pct_token');
        setState({ user: null, token: null, loading: false, error: null });
      }
    };
    loadUser();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await api.post<any>('/auth/login', { email, password });
      if (data.success) {
        // Clear ALL user-scoped keys to prevent cross-user data bleed
        sessionStorage.removeItem('pct_token');
        sessionStorage.removeItem('pct_user');
        sessionStorage.removeItem('premierTab');
        // Store new session
        sessionStorage.setItem('pct_token', data.token);
        setState({ user: data.user, token: data.token, loading: false, error: null });
        navigate(data.redirectTo);
      }
    } catch (err: any) {
      setState((s) => ({ ...s, loading: false, error: err.message }));
    }
  }, [navigate]);

  const register = useCallback(async (regData: { firstName: string; lastName: string; email: string; teamId: string; password: string }) => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await api.post<any>('/auth/register', regData);
      if (data.success) {
        sessionStorage.setItem('pct_token', data.token);
        setState({ user: data.user, token: data.token, loading: false, error: null });
        navigate(ROLE_REDIRECTS[data.user.role as UserRole]);
      }
    } catch (err: any) {
      setState((s) => ({ ...s, loading: false, error: err.message }));
    }
  }, [navigate]);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    sessionStorage.removeItem('pct_token');
    sessionStorage.removeItem('pct_user');
    sessionStorage.removeItem('premierTab');
    setState({ user: null, token: null, loading: false, error: null });
    navigate('/login');
  }, [navigate]);

  const clearError = useCallback(() => {
    setState((s) => ({ ...s, error: null }));
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, register, logout, clearError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
